import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal, Button, Icon, Progress, Segment, Label, Header, List, Dropdown
} from 'semantic-ui-react';
import { API, showError, showSuccess } from '../helpers';

const statusColors = {
  pending: 'grey',
  running: 'yellow',
  done: 'green',
  error: 'red',
};

const statusIcons = {
  pending: 'clock outline',
  running: 'spinner',
  done: 'check',
  error: 'warning sign',
};

const SwitchProfileModal = ({ open, onClose }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState('');
  const [progress, setProgress] = useState(null);
  const [executing, setExecuting] = useState(false);

  const loadProfiles = useCallback(async () => {
    try {
      const res = await API.get('/api/model-manager/profiles');
      const { success, data } = res.data;
      if (success) setProfiles(data || []);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    if (open) {
      setProgress(null);
      setExecuting(false);
      setSelectedProfile('');
      loadProfiles();
    }
  }, [open, loadProfiles]);

  // Poll progress while executing
  useEffect(() => {
    if (!executing || !selectedProfile) return;
    const timer = setInterval(async () => {
      try {
        const res = await API.get(`/api/model-manager/profiles/${selectedProfile}/status`);
        const { success, data } = res.data;
        if (success && data) {
          setProgress(data);
          if (data.status === 'done' || data.status === 'error') {
            setExecuting(false);
            if (data.status === 'done') showSuccess('模式切换完成');
          }
        }
      } catch (e) { /* silent */ }
    }, 2000);
    return () => clearInterval(timer);
  }, [executing, selectedProfile]);

  const handleExecute = async () => {
    if (!selectedProfile) {
      showError('请选择切换预设');
      return;
    }
    setExecuting(true);
    try {
      const res = await API.post(`/api/model-manager/profiles/${selectedProfile}/execute`);
      const { success, message } = res.data;
      if (!success) {
        showError(message);
        setExecuting(false);
      }
    } catch (e) {
      showError(e.message);
      setExecuting(false);
    }
  };

  const profileOptions = profiles.map(p => ({
    key: p.profile_name,
    text: `${p.profile_name} ${p.is_active ? '(当前)' : ''} - ${p.description || ''}`,
    value: p.profile_name,
    disabled: p.is_active,
  }));

  const pct = progress && progress.total_steps > 0
    ? Math.round((progress.current_step / progress.total_steps) * 100)
    : 0;

  return (
    <Modal open={open} onClose={onClose} size='small' closeOnDimmerClick={!executing}>
      <Modal.Header>
        <Icon name='exchange' /> 模式切换
      </Modal.Header>
      <Modal.Content>
        {!executing && !progress ? (
          <>
            <Header as='h5'>选择切换预设</Header>
            <Dropdown
              selection
              fluid
              placeholder='选择预设...'
              options={profileOptions}
              value={selectedProfile}
              onChange={(e, { value }) => setSelectedProfile(value)}
            />
            {profiles.filter(p => p.is_active).length > 0 && (
              <Segment basic style={{ padding: '8px 0' }}>
                <Label color='blue'>
                  当前模式: {profiles.find(p => p.is_active)?.profile_name}
                </Label>
              </Segment>
            )}
          </>
        ) : (
          <>
            <Header as='h5'>
              切换进度: {selectedProfile}
              {progress && (
                <Label
                  color={statusColors[progress.status]}
                  size='small'
                  style={{ marginLeft: 8 }}
                >
                  {progress.status}
                </Label>
              )}
            </Header>
            <Progress
              percent={pct}
              color={progress?.status === 'error' ? 'red' : 'blue'}
              active={executing}
              progress
            />
            {progress && progress.steps && (
              <List divided>
                {progress.steps.map((step, i) => (
                  <List.Item key={i}>
                    <List.Icon
                      name={statusIcons[step.status] || 'circle'}
                      color={statusColors[step.status]}
                      loading={step.status === 'running'}
                    />
                    <List.Content>
                      <List.Header>
                        {step.action === 'stop' ? '停止' : step.action === 'start' ? '启动' : step.action} {step.target}
                      </List.Header>
                      {step.message && <List.Description>{step.message}</List.Description>}
                    </List.Content>
                  </List.Item>
                ))}
              </List>
            )}
            {progress?.error && (
              <Segment color='red' inverted>
                <Icon name='warning sign' /> {progress.error}
              </Segment>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {!executing && !progress && (
          <Button primary onClick={handleExecute} disabled={!selectedProfile}>
            <Icon name='play' /> 执行切换
          </Button>
        )}
        <Button
          onClick={() => {
            setProgress(null);
            setExecuting(false);
            setSelectedProfile('');
            onClose();
          }}
          disabled={executing}
        >
          {progress ? '关闭' : '取消'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default SwitchProfileModal;
